import { Components, Theme, alpha } from '@mui/material';
import { alertTheme, alertTitleTheme } from './components/alertTheme';
import { buttonTheme } from './components/buttonTheme';
import { chipTheme } from './components/chipTheme';
import { paperTheme } from './components/paperTheme';
import { raFabTheme } from './components/raFabTheme';
import { raList } from './components/raList';
import { raToolBarTheme } from './components/raToolBarTheme';
import { selectTheme } from './components/selectTheme';
import { svgIconTheme } from './components/svgIconTheme';
import { tabTheme } from './components/tabTheme';
import { tableRowTheme } from './components/tableRowTheme';
import { toolBarTheme } from './components/toolBarTheme';
import { typography } from './typography';

export const muiComponentsTheme: Components<Omit<Theme, 'components'>> = {
    MuiToolbar: toolBarTheme,
    MuiSvgIcon: svgIconTheme,
    MuiAppBar: {
        styleOverrides: {
            root: {
                paddingRight: '0 !important',
                background: 'unset',
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            sizeSmall: {
                padding: 2,
            },
        },
    },
    MuiTableRow: tableRowTheme,
    MuiPaper: paperTheme,
    MuiCardActionArea: {
        styleOverrides: {
            root: {
                height: '100%',
            },
        },
    },
    MuiLink: {
        defaultProps: {
            underline: 'none',
        },
        styleOverrides: {
            root: ({ theme }) => ({
                cursor: 'pointer',
                color: theme.palette.info.main,
                '&:hover': {
                    fontWeight: 500,
                },

                '.MuiTypography-root:first-of-type': {
                    color: theme.palette.info.main,
                    '&:hover': {
                        fontWeight: 500,
                    },
                },
            }),
        },
    },
    MuiDialog: {
        styleOverrides: {
            root: ({ theme }) => ({
                '& .MuiDialog-paper': {
                    borderRadius: theme.shape.borderRadius * 4,
                    '& > form': {
                        overflow: 'auto',
                    },
                },
            }),
        },
    },
    MuiDrawer: {
        styleOverrides: {
            root: ({ theme }) => ({
                '& .RaSidebar-paper': {
                    boxShadow: theme.shape.boxShadow,
                },
                '& .MuiDrawer-paper:not(.RaSidebar-paper)': {
                    boxShadow: theme.shape.shadow,
                },
            }),
        },
    },
    MuiTextField: {
        defaultProps: {
            variant: 'outlined',
        },
        styleOverrides: {
            root: {
                margin: 0,
                'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                },
                'input[type=number]': {
                    MozAppearance: 'textfield',
                },
                '&.no-stretch-input': {
                    width: 'unset',
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: ({ theme }) => ({
                backgroundColor: theme.palette.background.paper,
                borderRadius: theme.shape.borderRadius * 2,
            }),
        },
    },
    MuiSelect: selectTheme,
    MuiInputLabel: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.text.secondary,
            }),
        },
    },
    MuiFormHelperText: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.text.secondary,
                backgroundColor: 'unset',
            }),
        },
    },
    MuiInputBase: {
        styleOverrides: {
            input: ({ theme }) => ({
                boxSizing: 'content-box !important' as 'content-box',
                '&::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 1,
                },
            }),
            root: ({ theme, ownerState }) => ({
                '.text-color & .MuiSvgIcon-root,.text-color & .MuiSelect-select': {
                    color: ownerState.color && theme.palette[ownerState.color].main,
                },
            }),
        },
    },
    MuiChip: chipTheme,
    MuiFab: raFabTheme,
    MuiButton: buttonTheme,
    MuiTab: tabTheme,
    MuiAlertTitle: alertTitleTheme,
    MuiAlert: alertTheme,
    MuiCheckbox: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: {
                '&:hover': {
                    backgroundColor: 'transparent',
                },
            },
        },
    },
    MuiRadio: {
        defaultProps: {
            disableRipple: true,
        },
        styleOverrides: {
            root: {
                '&:hover': {
                    backgroundColor: 'transparent',
                },
            },
        },
    },
    MuiStack: {
        defaultProps: {
            spacing: 2,
        },
    },
    MuiDivider: {
        styleOverrides: {
            withChildren: {
                ...typography.body1,
                fontWeight: 400,
                '&::before': {
                    top: 0,
                },
                '&::after': {
                    top: 0,
                },
                '& .MuiDivider-wrapper': {
                    paddingLeft: 16,
                    paddingRight: 16,
                },
            },
        },
    },
    MuiTypography: {
        defaultProps: {
            variantMapping: {
                h1: 'h1',
                h2: 'h2',
                h3: 'h3',
                subtitle1: 'h6',
                subtitle2: 'h6',
                subtitle3: 'h6',
                subtitle4: 'h6',
                body1: 'p',
                body2: 'p',
                body3: 'p',
            },
        },
        styleOverrides: {
            root: ({ theme }) => ({
                '&.MuiTypography-gutterBottom': {
                    marginBottom: theme.spacing(2),
                },
            }),
        },
    },
    MuiAccordion: {
        defaultProps: {
            elevation: 0,
            disableGutters: true,
            square: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                border: `1px solid ${theme.palette.dividerDark}`,
                boxShadow: theme.shape.boxShadow,
                '&:before': {
                    display: 'none',
                },
                '& .MuiAccordionSummary-root': {
                    padding: theme.spacing(0, 3),
                },
                '& .MuiAccordionSummary-content': {
                    margin: theme.spacing(2, 0),
                },
                '& .MuiAccordionDetails-root': {
                    padding: theme.spacing(0, 3),
                    margin: theme.spacing(2, 0),
                },
            }),
        },
    },
    MuiMenu: {
        styleOverrides: {
            list: ({ theme }) => ({
                padding: theme.spacing(0.5, 0),
            }),
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: ({ theme, ownerState }) => ({
                color: ownerState.color && theme.palette[ownerState.color].main,
                '&:hover': {
                    backgroundColor: ownerState.color && theme.palette[ownerState.color].light,
                },
                '&.MuiMenuItem-root.Mui-selected': {
                    backgroundColor: ownerState.color && theme.palette[ownerState.color].light,
                },
            }),
        },
    },
};

export const raComponentsTheme = (theme: Theme) => ({
    RaSimpleShowLayout: {
        styleOverrides: {
            root: {
                padding: '0 0 30px 0',
            },
        },
    },
    RaSimpleForm: {
        styleOverrides: {
            root: {
                padding: 0,
            },
        },
    },
    RaCreate: {
        styleOverrides: {
            root: {
                '& .RaCreate-main': {
                    margin: 0,
                },
            },
        },
    },
    RaEdit: {
        styleOverrides: {
            root: {
                '& .RaEdit-main': {
                    margin: 0,
                },
            },
        },
    },
    RaList: raList(theme),
    RaToolbar: raToolBarTheme(theme),
    RaLabeled: {
        styleOverrides: {
            root: {
                '& .RaLabeled-label': {
                    fontSize: '14px !important',
                },
            },
        },
    },
    RaSearchInput: {
        styleOverrides: {
            root: {
                backgroundColor: theme.palette.divider,
                '& legend': { display: 'none' },
            },
        },
    },
    RaFileInput: {
        styleOverrides: {
            root: {
                '& .RaFileInput-removeButton': {
                    marginTop: theme.spacing(2),
                },
                '& .RaFileInput-removeButton .MuiIconButton-root': {
                    height: 40,
                    width: 40,
                    marginRight: theme.spacing(1),
                },
            },
        },
    },
    RaListToolbar: {
        styleOverrides: {
            root: {
                [theme.breakpoints.down('sm')]: {
                    backgroundColor: 'unset',
                },
            },
        },
    },
    RaTopToolbar: {
        styleOverrides: {
            root: {
                [theme.breakpoints.down('sm')]: {
                    backgroundColor: 'unset',
                },
            },
        },
    },
    RaAppBar: {
        styleOverrides: {
            root: {
                ['& .RaAppBar-toolbar']: {
                    height: 'unset',
                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                    backdropFilter: 'blur(2px)',
                },
            },
        },
    },
    RaReferenceField: {
        styleOverrides: {
            root: {
                '.MuiTypography-root:first-of-type': {
                    color: theme.palette.info.main,
                    '&:hover': {
                        fontWeight: 500,
                    },
                },
                '.MuiChip-label': {
                    color: theme.palette.info.main,
                },
                '.MuiChip-filled': {
                    backgroundColor: theme.palette.info.light,
                },
            },
        },
    },
});
